import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import 'ol/ol.css';
import styles from './Home.module.scss';
import bgHero from './assets/background.webp';
import imgRestaurante from './assets/restaurante.webp';
import imgHotel from './assets/hotel.webp';
import imgCultura from './assets/cultura.webp';
import imgCroa from './assets/croa.jpg';
import imgSaoCristovao from './assets/cristo-redentor-sao-cristovao-se.webp';
import imgMangueSeco from './assets/mangue-seco.webp';
import imgMercados from './assets/mercado-centrais.webp';

/* ─── Scroll-reveal hook ──────────────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.revealed);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ─── Inline SVG icons ───────────────────────────────────────────────────── */
const Icon = {
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="3" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  fork: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><line x1="7" y1="2" x2="7" y2="22" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6h3.5a1.5 1.5 0 0 1 0 3H14v4" />
    </svg>
  ),
  hotel: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  house: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
      <path d="M2 20h20M4 20V10l8-8 8 8v10" /><rect x="9" y="14" width="6" height="6" />
    </svg>
  ),
  mapPinFilled: (
    <svg viewBox="0 0 24 24" fill="#ff5722" width="24" height="24">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
  ),
};

/* ─── Component ───────────────────────────────────────────────────────────── */
function Home() {
  useScrollReveal();
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const initialMap = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat([-37.0715, -10.9472]),
        zoom: 9,
      }),
      controls: [],
    });

    return () => {
      initialMap.setTarget(undefined);
    };
  }, []);

  return (
    <div className={styles.page}>


      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div
          className={styles.heroBg}
          style={{ backgroundImage: `url(${bgHero})` }}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroFade} />

        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Descubra Sergipe</h1>
          <p className={styles.heroSub}>
            Explore as cores, sabores e histórias do estado mais encantador do Brasil.
          </p>

          <div className={styles.searchBar}>
            <label className={styles.searchField}>
              <span className={styles.searchIconWrap}>{Icon.pin}</span>
              <input
                type="text"
                placeholder="Para onde você quer ir?"
                className={styles.searchInput}
              />
            </label>

            <div className={styles.searchDivider} />

            <div className={styles.filterWrapper}>
              <button 
                className={styles.searchCat}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <span className={styles.searchIconWrap}>{Icon.plus}</span>
                <span>Todas as Categorias</span>
                <span className={`${styles.chevron} ${isFilterOpen ? styles.chevronOpen : ''}`}>
                  {Icon.chevron}
                </span>
              </button>

              <div className={`${styles.dropdown} ${isFilterOpen ? styles.dropdownActive : ''}`}>
                <div className={styles.dropdownItem} onClick={() => navigate('/estabelecimentos')}>
                  <span className={styles.dropIcon} style={{ color: '#e86c2c' }}>{Icon.fork}</span>
                  <span>Restaurantes</span>
                </div>
                <div className={styles.dropdownItem} onClick={() => navigate('/estabelecimentos')}>
                  <span className={styles.dropIcon} style={{ color: '#4a7fd6' }}>{Icon.hotel}</span>
                  <span>Hotéis</span>
                </div>
                <div className={styles.dropdownItem} onClick={() => navigate('/estabelecimentos')}>
                  <span className={styles.dropIcon} style={{ color: '#8b5cf6' }}>{Icon.house}</span>
                  <span>Cultura</span>
                </div>
              </div>
            </div>

            <button className={styles.searchBtn} onClick={() => navigate('/estabelecimentos')}>
              {Icon.search}
              Buscar
            </button>
          </div>
        </div>
      </section>

      {/* ── MAIN ───────────────────────────────────────────────────────── */}
      <main className={styles.main}>

        {/* CATEGORIES */}
        <section className={styles.section} data-reveal>
          <div className={styles.sectionHeader}>
            <div>
              <span className={styles.sectionLabel}>O Que Fazer</span>
              <h2 className={styles.sectionTitle}>Explore por Categorias</h2>
            </div>
            <a href="/categorias" className={styles.sectionLink}>
              Ver todas as opções {Icon.arrowRight}
            </a>
          </div>

          <div className={styles.categoryGrid}>
            {[
              {
                icon: Icon.fork,
                iconBg: '#fff5ec',
                iconColor: '#e86c2c',
                title: 'Restaurantes',
                desc: 'Descubra a riqueza da culinária sergipana, do caranguejo à carne de sol.',
                img: imgRestaurante,
              },
              {
                icon: Icon.hotel,
                iconBg: '#eef4ff',
                iconColor: '#4a7fd6',
                title: 'Hotéis',
                desc: 'Conforto e hospitalidade nos melhores pontos turísticos do estado.',
                img: imgHotel,
              },
              {
                icon: Icon.house,
                iconBg: '#f4eeff',
                iconColor: '#8b5cf6',
                title: 'Cultura',
                desc: 'Mergulhe no artesanato, museus e manifestações folclóricas únicas.',
                img: imgCultura,
              },
            ].map((card, i) => (
              <article
                key={card.title}
                className={styles.categoryCard}
                data-reveal
                style={{ '--delay': `${i * 110}ms` } as React.CSSProperties}
              >
                <div className={styles.categoryImgWrap}>
                  <img
                    src={card.img}
                    alt={card.title}
                    className={styles.categoryImg}
                  />
                  <div className={styles.categoryImgOverlay} />
                </div>
                <div className={styles.categoryBody}>
                  <span
                    className={styles.categoryIconBox}
                    style={{ background: card.iconBg, color: card.iconColor }}
                  >
                    {card.icon}
                  </span>
                  <h3 className={styles.categoryName}>{card.title}</h3>
                  <p className={styles.categoryDesc}>{card.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* MAP */}
        <section className={styles.mapSection} data-reveal>
          <div className={styles.mapInfo}>
            <h2 className={styles.mapTitle}>Explore o Mapa</h2>
            <p className={styles.mapDesc}>
              Navegue geograficamente por todos os pontos de interesse.
              De praias desertas a centros históricos centenários.
            </p>
            <button className={styles.mapBtn} onClick={() => navigate('/mapa')}>
              Abrir Mapa Interativo
            </button>
          </div>
          <div className={styles.mapPreview} ref={mapRef}>
            {/* The OpenLayers map renders here */}
          </div>
        </section>

        {/* HIGHLIGHTS — bento grid */}
        <section className={styles.section} data-reveal>
          <div className={styles.sectionHeader}>
            <div>
              <span className={styles.sectionLabel}>Destinos em Destaque</span>
              <h2 className={styles.sectionTitle}>O melhor de Sergipe</h2>
            </div>
          </div>

          <div className={styles.bento}>
            <article
              className={`${styles.bentoCard} ${styles.bentoMain}`}
              data-reveal
              style={{ '--delay': '0ms' } as React.CSSProperties}
            >
              <div
                className={styles.bentoImg}
                style={{ backgroundImage: `url(${imgCroa})` }}
              />
              <div className={styles.bentoGrad} />
              <div className={styles.bentoText}>
                <span className={styles.bentoBadge}>Mais Visitado</span>
                <h3 className={styles.bentoName}>Croa do Goré</h3>
                <p className={styles.bentoDesc}>Um paraíso flutuante que surge apenas na maré baixa.</p>
              </div>
            </article>

            <div className={styles.bentoCol}>
              <article
                className={`${styles.bentoCard} ${styles.bentoTop}`}
                data-reveal
                style={{ '--delay': '100ms' } as React.CSSProperties}
              >
                <div
                  className={styles.bentoImg}
                  style={{ backgroundImage: `url(${imgSaoCristovao})` }}
                />
                <div className={styles.bentoGrad} />
                <div className={styles.bentoText}>
                  <h3 className={styles.bentoName}>São Cristóvão</h3>
                  <p className={styles.bentoDesc}>Quarta cidade mais antiga do Brasil.</p>
                </div>
              </article>

              <div className={styles.bentoRow}>
                {[
                  { name: 'Mangue Seco', img: imgMangueSeco },
                  { name: 'Mercados Centrais', img: imgMercados }
                ].map((item, i) => (
                  <article
                    key={item.name}
                    className={`${styles.bentoCard} ${styles.bentoSmall}`}
                    data-reveal
                    style={{ '--delay': `${180 + i * 70}ms` } as React.CSSProperties}
                  >
                    <div
                      className={styles.bentoImg}
                      style={{ backgroundImage: `url(${item.img})` }}
                    />
                    <div className={styles.bentoGrad} />
                    <div className={styles.bentoText}>
                      <h3 className={styles.bentoNameSm}>{item.name}</h3>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CURIOSIDADES CTA */}
<section className={styles.curiositiesCta} data-reveal>
  <span className={styles.curiositiesTag}>Explore mais</span>

  <h2 className={styles.curiositiesTitle}>
    Conheça histórias e curiosidades de Sergipe
  </h2>

  <p className={styles.curiositiesText}>
    Descubra fatos interessantes sobre cidades históricas, paisagens,
    tradições e lugares marcantes do estado.
  </p>

  <button
    type="button"
    className={styles.curiositiesButton}
    onClick={() => navigate('/curiosidades')}
  >
    Explorar curiosidades
    {Icon.arrowRight}
  </button>
</section>

      </main>
    </div>
  );
}

export default Home;